import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, CheckCircle, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

type MarketingFactCheckDemoProps = {
  className?: string;
};

const demoFactCheck = {
  score: 94,
  assessment: "This paragraph accurately reflects Arcitext's tone-enforcement workflow and benefit promise.",
  analysis:
    "Mentions of ‘brand voice locks’ and ‘analysis audit’ align with Arcitext features. Keep quantified improvements (4× faster launches) for credibility.",
  sources: [
    {
      title: "Arcitext documentation — Brand Voice Locks",
      relevance: "Explains how guardrails maintain tone across documents.",
      url: "https://arcitext.com/docs/brand-voice-locks",
    },
    {
      title: "Case study: Studio N",
      relevance: "Shows time saved by the instant audit workflow.",
      url: "https://arcitext.com/customers/studio-n",
    },
  ],
};

export const MarketingFactCheckDemo = ({ className }: MarketingFactCheckDemoProps) => {
  return (
    <Card className={cn("max-w-xl", className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <CheckCircle className="h-4 w-4" />
          Fact check
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center gap-2 text-sm">
          <Badge className="bg-emerald-500 text-white">{demoFactCheck.score}/100</Badge>
          <span className="font-medium">Excellent accuracy</span>
        </div>

        <div>
          <h4 className="text-sm font-semibold mb-1">Assessment</h4>
          <div className="space-y-2">
            <div className="h-3 w-11/12 rounded bg-foreground/10" />
            <div className="h-3 w-3/6 rounded bg-foreground/10" />
            <div className="h-3 w-3/5 rounded bg-foreground/10" />
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold mb-1">Detailed analysis</h4>
          <div className="space-y-2">
            <div className="h-3 w-11/12 rounded bg-foreground/10" />
            <div className="h-3 w-5/6 rounded bg-foreground/10" />
            <div className="h-3 w-7/12 rounded bg-foreground/10" />
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-semibold">Sources</h4>
          <div className="space-y-3">
            {demoFactCheck.sources.map((source) => (
              <div key={source.url} className="rounded-lg border border-border/70 bg-muted/20 p-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-1">
                    <div className="h-3 w-7/12 rounded bg-foreground/10" />
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Copy markdown link">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Open in new tab">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="h-2 w-11/12 rounded bg-primary/20" />
                </div>

              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MarketingFactCheckDemo;
